// SummaryCard.tsx
import React from "react";
import { Professional, Service, Location } from "./types";

interface SummaryCardProps {
  professional: Professional;
  selectedService: Service | null;
  selectedLocation: Location | null;
  selectedDate: string | null;
  selectedTime: string | null;
  onConfirm: () => void;
}

const SummaryCard: React.FC<SummaryCardProps> = ({
  professional,
  selectedService,
  selectedLocation,
  selectedDate,
  selectedTime,
  onConfirm,
}) => {
  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("es-ES", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const isFormComplete =
    selectedService && selectedLocation && selectedDate && selectedTime;

  // Function to determine what's missing
  const getMissingItems = () => {
    const missing = [];
    if (!selectedService) missing.push("servicio");
    if (!selectedLocation) missing.push("ubicación");
    if (!selectedDate) missing.push("fecha");
    if (!selectedTime) missing.push("hora");
    return missing;
  };

  const missingItems = getMissingItems();
  const hasMissingItems = missingItems.length > 0;

  return (
    <div id="summary" className="card summary-card">
      <h4>Resumen de tu Cita</h4>

      {/* Missing Items Alert - Prominently displayed */}
      {hasMissingItems && (
        <div className="missing-items-alert">
          <div className="missing-items-header">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 9V11M12 15H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0377 2.66667 10.2679 4L3.33975 16C2.56995 17.3333 3.53223 19 5.07183 19Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>Completa tu reservación</span>
          </div>
          <p className="missing-items-message">
            Faltan por seleccionar: <strong>{missingItems.join(", ")}</strong>
          </p>
          <div className="missing-items-progress">
            <div
              className="missing-items-progress-bar"
              style={{
                width: `${((4 - missingItems.length) / 4) * 100}%`,
              }}
            ></div>
          </div>
          <p className="missing-items-hint">
            Completa todos los pasos para confirmar tu cita
          </p>
        </div>
      )}

      <p>
        <strong>Profesional:</strong> {professional.name}
      </p>
      <hr
        style={{
          borderColor: "var(--neutral-200)",
          margin: "var(--space-sm) 0",
        }}
      />

      <p style={{ color: "var(--color-accent)", fontWeight: 700 }}>
        Servicio Seleccionado
      </p>
      {selectedService ? (
        <>
          <p>
            <strong>Tipo de Cita:</strong> {selectedService.name}
          </p>
          <p>
            <strong>Duración:</strong> {selectedService.duration} minutos
          </p>
          <p>
            <strong>Costo:</strong>{" "}
            <span style={{ color: "var(--color-accent)", fontWeight: 700 }}>
              ${selectedService.price.toFixed(2)}
            </span>
          </p>
        </>
      ) : (
        <p style={{ color: "var(--error)", fontStyle: "italic" }}>
          ⚠️ No se ha seleccionado un servicio
        </p>
      )}

      <hr
        style={{
          borderColor: "var(--neutral-200)",
          margin: "var(--space-sm) 0",
        }}
      />

      <p style={{ color: "var(--color-accent)", fontWeight: 700 }}>
        Fecha y Hora
      </p>
      {selectedDate && selectedTime ? (
        <>
          <p>
            <strong>Día:</strong> {formatDate(selectedDate)}
          </p>
          <p>
            <strong>Hora:</strong> {selectedTime}
          </p>
        </>
      ) : (
        <p style={{ color: "var(--error)", fontStyle: "italic" }}>
          ⚠️{" "}
          {!selectedDate && !selectedTime
            ? "No se ha seleccionado fecha y hora"
            : !selectedDate
            ? "No se ha seleccionado fecha"
            : "No se ha seleccionado hora"}
        </p>
      )}

      <hr
        style={{
          borderColor: "var(--neutral-200)",
          margin: "var(--space-sm) 0",
        }}
      />

      <p style={{ color: "var(--color-accent)", fontWeight: 700 }}>Ubicación</p>
      {selectedLocation ? (
        <p>
          <strong>Lugar:</strong> {selectedLocation.name}
        </p>
      ) : (
        <p style={{ color: "var(--error)", fontStyle: "italic" }}>
          ⚠️ No se ha seleccionado una ubicación
        </p>
      )}

      <hr
        style={{
          borderColor: "var(--neutral-200)",
          margin: "var(--space-md) 0",
        }}
      />

      <button
        className="btn btn--primary btn--large"
        style={{
          width: "100%",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
        }}
        onClick={onConfirm}
        disabled={!isFormComplete}
      >
        {!isFormComplete && (
          <div
            className="button-progress-overlay"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              width: `${((4 - missingItems.length) / 4) * 100}%`,
              backgroundColor: "rgba(255, 255, 255, 0.3)",
              transition: "width 0.3s ease",
            }}
          ></div>
        )}
        {isFormComplete
          ? "Confirmar cita"
          : `Completa los pasos (${4 - missingItems.length}/4)`}
      </button>

      {/* Progress indicator */}
      {hasMissingItems && (
        <div
          style={{
            textAlign: "center",
            marginTop: "var(--space-sm)",
            fontSize: "0.9rem",
            color: "var(--neutral-300)",
          }}
        >
          Progreso: {4 - missingItems.length} de 4 completados
        </div>
      )}
    </div>
  );
};

export default SummaryCard;
